// src/pages/AutoArchivio.jsx - Enhanced with pagination and improved filtering
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { supabase } from '@/lib/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from "framer-motion";
import { AlertTriangle, Archive, Calendar, Car, Download, MapPin, RefreshCw, Search, Trash2, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import AutoFilters from "../components/auto/AutoFilters";

export default function AutoArchivioPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    stato: "all",
    cliente: "all"
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    auto: null
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const queryClient = useQueryClient();

  // Load archived autos
  const { data: autoArchivio = [], isLoading: autoLoading } = useQuery({
    queryKey: ['autoarchivio'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('autoarchivio')
        .select('*')
        .order('archived_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }
  });

  // Load reference data for display
  const { data: clienti = [], isLoading: clientiLoading } = useQuery({
    queryKey: ['clienti'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clienti')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: stati = [], isLoading: statiLoading } = useQuery({
    queryKey: ['stati'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stati')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: tettoie = [], isLoading: tettoieLoading } = useQuery({
    queryKey: ['tettoie'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tettoie')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: sezioni = [], isLoading: sezioniLoading } = useQuery({
    queryKey: ['sezioni'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sezioni')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: campate = [], isLoading: campateLoading } = useQuery({
    queryKey: ['campate'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campate')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  const isLoading = autoLoading || clientiLoading || statiLoading || 
                   tettoieLoading || sezioniLoading || campateLoading;

  // Delete mutation for archived autos
  const deleteArchivedAutoMutation = useMutation({
    mutationFn: async (autoId) => {
      const { error } = await supabase
        .from('autoarchivio')
        .delete()
        .eq('id', autoId);

      if (error) {
        console.error('Delete error:', error);
        throw new Error('Errore durante l\'eliminazione: ' + error.message);
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoarchivio'] });
    },
    onError: (error) => {
      console.error('Delete mutation error:', error);
      alert(error.message || 'Errore durante l\'eliminazione del record archiviato.');
    }
  });

  // NEW: Enhanced filtering logic that works across ALL data
  const filteredAutoArchivio = useMemo(() => {
    return autoArchivio.filter(a => {
      const matchesSearch = !searchTerm || 
        a.targa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.marca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.modello?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.numero_telaio?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStato = filters.stato === "all" || a.stato_id === filters.stato;
      const matchesCliente = filters.cliente === "all" || a.cliente_id === filters.cliente;

      return matchesSearch && matchesStato && matchesCliente;
    });
  }, [autoArchivio, searchTerm, filters]);

  // NEW: Pagination calculations
  const totalPages = Math.ceil(filteredAutoArchivio.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAutoArchivio = filteredAutoArchivio.slice(startIndex, endIndex);

  // NEW: Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, pageSize]);

  // NEW: Ensure current page is valid when data changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // NEW: Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Refresh function to force reload from database
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidate and refetch all queries to get fresh data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['autoarchivio'] }),
        queryClient.invalidateQueries({ queryKey: ['clienti'] }),
        queryClient.invalidateQueries({ queryKey: ['stati'] }),
        queryClient.invalidateQueries({ queryKey: ['tettoie'] }),
        queryClient.invalidateQueries({ queryKey: ['sezioni'] }),
        queryClient.invalidateQueries({ queryKey: ['campate'] })
      ]);
      
      // Wait for all queries to refetch
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['autoarchivio'] }),
        queryClient.refetchQueries({ queryKey: ['clienti'] }),
        queryClient.refetchQueries({ queryKey: ['stati'] }),
        queryClient.refetchQueries({ queryKey: ['tettoie'] }),
        queryClient.refetchQueries({ queryKey: ['sezioni'] }),
        queryClient.refetchQueries({ queryKey: ['campate'] })
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle delete confirmation dialog
  const handleDeleteClick = (autoItem) => {
    setDeleteConfirmation({
      open: true,
      auto: autoItem
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmation.auto) {
      try {
        await deleteArchivedAutoMutation.mutateAsync(deleteConfirmation.auto.id);
        setDeleteConfirmation({ open: false, auto: null });
      } catch (error) {
        console.error('Error deleting archived auto:', error);
        // Error is already handled in mutation onError
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ open: false, auto: null });
  };

  // Helper functions
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "N/D";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/D";
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error("Error formatting date for display:", error);
      return "N/D";
    }
  };

  const getClienteName = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente ? `${cliente.nome} ${cliente.cognome}` : "Cliente sconosciuto";
  };

  const getStatoName = (statoId) => {
    const stato = stati.find(s => s.id === statoId);
    return stato ? stato.nome : "Stato sconosciuto";
  };

  const getCampataFullName = (campataId) => {
    if (!campataId) return "N/D";
    const campata = campate.find(c => c.id === campataId);
    if (!campata) return "N/D";
    const sezione = sezioni.find(s => s.id === campata.sezione_id);
    if (!sezione) return campata.nome;
    const tettoia = tettoie.find(t => t.id === sezione.tettoia_id);
    if (!tettoia) return `${sezione.nome} / ${campata.nome}`;
    return `${tettoia.nome} / ${sezione.nome} / ${campata.nome}`;
  };

  // NEW: Enhanced CSV Export function (exports filtered data)
  const exportToCSV = () => {
    const headers = [
      'Targa',
      'Marca', 
      'Modello',
      'Numero Telaio',
      'Cliente',
      'Data Ingresso',
      'Data Uscita',
      'Data Archiviazione',
      'Stato',
      'Posizione',
      'Km',
      'Note'
    ];

    const csvData = filteredAutoArchivio.map(autoItem => [
      autoItem.targa || '',
      autoItem.marca || '',
      autoItem.modello || '',
      autoItem.numero_telaio || '',
      getClienteName(autoItem.cliente_id),
      autoItem.data_ingresso ? new Date(autoItem.data_ingresso).toLocaleDateString('it-IT') : '',
      autoItem.data_uscita ? new Date(autoItem.data_uscita).toLocaleDateString('it-IT') : '',
      autoItem.archived_at ? new Date(autoItem.archived_at).toLocaleDateString('it-IT') : '',
      getStatoName(autoItem.stato_id),
      getCampataFullName(autoItem.campata_id),
      autoItem.km ?? 0,
      autoItem.note || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `archivio-auto-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-600">Caricamento archivio...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 md:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2 flex items-center gap-3">
                <Archive className="w-8 h-8 text-slate-600" />
                Archivio Auto
              </h1>
              <p className="text-slate-600">
                Visualizza e gestisci le auto archiviate
                {filteredAutoArchivio.length !== autoArchivio.length && (
                  <span className="ml-2 text-sm text-blue-600">
                    ({filteredAutoArchivio.length} di {autoArchivio.length} auto visualizzate)
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="bg-white hover:bg-slate-50 border-slate-300 text-slate-700 shadow-sm"
                disabled={filteredAutoArchivio.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta CSV {filteredAutoArchivio.length !== autoArchivio.length && `(${filteredAutoArchivio.length})`}
              </Button>
              <Button
                onClick={handleRefresh}
                disabled={isRefreshing}
                variant="outline"
                className="bg-white hover:bg-slate-50 border-slate-300 text-slate-700 shadow-sm"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Aggiornando...' : 'Aggiorna'}
              </Button>
              <div className="bg-orange-50 text-orange-700 px-4 py-2 rounded-lg border border-orange-200">
                <span className="text-sm font-medium">Archivio</span>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Cerca per targa, marca, modello, telaio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 glass-effect border-0 shadow-lg"
                />
              </div>
            </div>
            
            <div className="md:w-auto w-full">
              <AutoFilters
                filters={filters}
                setFilters={setFilters}
                clienti={clienti}
                stati={stati}
              />
            </div>
          </div>

          {/* NEW: Pagination Controls - Top */}
          <div className="mb-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredAutoArchivio.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 20, 50, 100]}
              showPageSizeSelector={true}
              showInfo={true}
            />
          </div>

          {/* Archive Table */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
            {filteredAutoArchivio.length === 0 ? (
              <div className="p-8 text-center">
                <Archive className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">Nessuna auto archiviata</h3>
                <p className="text-slate-500 mb-4">
                  {searchTerm || filters.stato !== "all" || filters.cliente !== "all" 
                    ? "Nessun risultato corrisponde ai filtri di ricerca"
                    : "L'archivio è vuoto"
                  }
                </p>
                {(searchTerm || filters.stato !== "all" || filters.cliente !== "all") && (
                  <Button
                    onClick={() => {
                      setSearchTerm("");
                      setFilters({ stato: "all", cliente: "all" });
                    }}
                    variant="outline"
                    className="text-slate-600"
                  >
                    Cancella filtri
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-700">
                          <div className="flex items-center space-x-2">
                            <Car className="w-4 h-4" />
                            <span>Auto</span>
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-slate-700">
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4" />
                            <span>Cliente</span>
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-slate-700">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4" />
                            <span>Data Ingresso</span>
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-slate-700">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4" />
                            <span>Data Uscita</span>
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-slate-700">
                          <div className="flex items-center space-x-2">
                            <Archive className="w-4 h-4" />
                            <span>Data Archiviazione</span>
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-slate-700">Stato</th>
                        <th className="text-left p-4 font-medium text-slate-700">
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4" />
                            <span>Posizione</span>
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-slate-700">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginatedAutoArchivio.map((autoItem, index) => (
                        <motion.tr
                          key={autoItem.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="p-4">
                            <div className="space-y-1">
                              <div className="font-semibold text-slate-800">
                                {autoItem.targa}
                              </div>
                              <div className="text-sm text-slate-600">
                                {autoItem.marca} {autoItem.modello}
                              </div>
                              {autoItem.numero_telaio && (
                                <div className="text-xs text-slate-500">
                                  Telaio: {autoItem.numero_telaio}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-slate-800">
                              {getClienteName(autoItem.cliente_id)}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-green-600" />
                              <span className="text-slate-800">
                                {formatDateForDisplay(autoItem.data_ingresso)}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center space-x-2">
                              <Calendar className={`w-4 h-4 ${autoItem.data_uscita ? 'text-red-600' : 'text-slate-400'}`} />
                              <span className={autoItem.data_uscita ? 'text-slate-800' : 'text-slate-500'}>
                                {autoItem.data_uscita ? formatDateForDisplay(autoItem.data_uscita) : "N/D"}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center space-x-2">
                              <Archive className="w-4 h-4 text-orange-600" />
                              <span className="text-slate-800 font-medium">
                                {formatDateForDisplay(autoItem.archived_at)}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                              <Archive className="w-3 h-3 mr-1" />
                              Archiviata
                            </span>
                            <div className="text-xs text-slate-600 mt-1">
                              {getStatoName(autoItem.stato_id)}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-slate-600">
                              {getCampataFullName(autoItem.campata_id)}
                            </div>
                          </td>
                          <td className="p-4">
                            <Button
                              onClick={() => handleDeleteClick(autoItem)}
                              variant="outline"
                              size="sm"
                              disabled={deleteArchivedAutoMutation.isPending}
                              className="hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Footer with count and refresh info */}
                <div className="bg-slate-50 px-4 py-3 border-t border-slate-200">
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>
                      Totale archiviate: {filteredAutoArchivio.length} auto
                      {filteredAutoArchivio.length !== autoArchivio.length && (
                        <span className="text-blue-600"> (filtrate da {autoArchivio.length})</span>
                      )}
                    </span>
                    <div className="flex items-center space-x-4">
                      <Archive className="w-4 h-4 text-orange-600" />
                      <span>Tutte le auto in questo elenco sono state archiviate</span>
                      {isRefreshing && (
                        <span className="flex items-center gap-1 text-blue-600">
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          Aggiornamento in corso...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* NEW: Pagination Controls - Bottom (only show if there are multiple pages) */}
          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={filteredAutoArchivio.length}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 50, 100]}
                showPageSizeSelector={false}
                showInfo={false}
              />
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmation.open} onOpenChange={handleCancelDelete}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Elimina record archiviato
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare definitivamente il record archiviato dell'auto{' '}
              <strong>{deleteConfirmation.auto?.targa}</strong>{' '}
              ({deleteConfirmation.auto?.marca} {deleteConfirmation.auto?.modello}).
              <br /><br />
              <strong>ATTENZIONE:</strong> Questa azione non può essere annullata e il record 
              verrà rimosso definitivamente dall'archivio.
              <br /><br />
              Sei sicuro di voler continuare?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelDelete}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
              disabled={deleteArchivedAutoMutation.isPending}
            >
              {deleteArchivedAutoMutation.isPending ? "Eliminando..." : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}